import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result } from './common/Result';
import type {
  EntryProvenance,
  ShowcaseEntries,
  TranslationMemoryEntry,
  TranslationMemoryEntryInput,
  TranslationMemoryEntryRepository,
} from '@src/repositories/TranslationMemoryEntryRepository';

const logger = LogManager.getLogger('service:TranslationMemoryEntry');

type RepositoryDeps = Pick<
  TranslationMemoryEntryRepository,
  'fetchForShowcase' | 'create' | 'update' | 'delete'
>;

/**
 * Orchestrates translation-memory entry CRUD over
 * `TranslationMemoryEntryRepository`, returning `Result`s. Holds a lazy
 * repository getter (repositories are constructed by `DatoClientStore` only
 * after auth is wired) — the exact shape of `TranslationMemoryCollectionService`.
 */
export class TranslationMemoryEntryService {
  constructor(private readonly getRepository: () => RepositoryDeps) {}

  fetchForShowcase(showcaseId: string): Promise<Result<ShowcaseEntries>> {
    if (!showcaseId)
      return Promise.resolve(err('VALIDATION', 'showcaseId is required'));
    return this.run('load translation memory entries', repo =>
      repo.fetchForShowcase(showcaseId),
    );
  }

  /**
   * `provenance` records how the entry was added (E12). Omitted ⇒ the
   * repository's `manual` default (Settings CRUD); the Translation Review
   * promote flow passes `{ origin: 'promoted' }` (PLAT-10852).
   */
  create(
    showcaseId: string,
    input: TranslationMemoryEntryInput,
    personId: string | null,
    provenance?: EntryProvenance,
  ): Promise<Result<TranslationMemoryEntry>> {
    const validation = this.validateInput(input);
    if (validation) return Promise.resolve(validation);
    if (!showcaseId)
      return Promise.resolve(err('VALIDATION', 'showcaseId is required'));
    return this.run('create translation memory entry', async repo => {
      const created = await repo.create(
        showcaseId,
        input,
        personId,
        provenance,
      );
      if (!created) throw new Error('Entry create returned null');
      return created;
    });
  }

  update(
    id: string,
    input: TranslationMemoryEntryInput,
    personId: string | null,
  ): Promise<Result<TranslationMemoryEntry>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    const validation = this.validateInput(input);
    if (validation) return Promise.resolve(validation);
    return this.run('update translation memory entry', async repo => {
      const updated = await repo.update(id, input, personId);
      if (!updated) throw new Error('Entry update returned null');
      return updated;
    });
  }

  delete(id: string): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    return this.run('delete translation memory entry', async repo => {
      const ok = await repo.delete(id);
      if (!ok) throw new Error('Entry delete failed');
    });
  }

  /** Shared field guards: a source term, a translation, and a source language. */
  private validateInput(
    input: TranslationMemoryEntryInput,
  ): Result<never> | null {
    if (!input.sourceTerm.trim())
      return err('VALIDATION', 'source term is required');
    if (!input.translation.trim())
      return err('VALIDATION', 'translation is required');
    if (!input.sourceLanguage?.id)
      return err('VALIDATION', 'source language is required');
    return null;
  }

  private async run<T>(
    description: string,
    fn: (repo: RepositoryDeps) => Promise<T>,
  ): Promise<Result<T>> {
    let repository: RepositoryDeps;
    try {
      repository = this.getRepository();
    } catch (e) {
      logger.error('Translation memory entry repository not initialized', e);
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }
    try {
      return ok(await fn(repository));
    } catch (e) {
      logger.error(`Failed to ${description}`, e);
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Unknown error',
        e,
      );
    }
  }
}
